<template>
  <div class="row poppins h-100">
    <div class="col-12 h-100">
      <div class="table-wrapper h-100 d-flex flex-column">
        <div class="filter-block">
          <div />
          <div v-if="isCommonAdmin" class="d-flex">
            <b-button variant="primary" size="lg" class="ml-3 pl-3 pr-4 d-flex align-items-center" @click="goToAdd">
              <BIconPlus class="mr-2" />
              {{ 'Create Milestone Group' | i18n }}
            </b-button>
            <b-button
              variant="outline-danger"
              size="lg"
              class="ml-3 pl-4 pr-4 d-flex align-items-center"
              :disabled="!selectedList.length"
              @click="deleteGroup"
            >
              <img :src="deleteIconUrl" class="mr-2" />
              {{ 'Remove Milestone Group' | i18n }}
            </b-button>
          </div>
        </div>
        <Table
          :update="getPage"
          :columns="columns"
          :rows="rows"
          :filter="filter"
          :updateFilter="updateFilter"
          :loadingInstance="loadingInstance"
          :setLoadingInstance="setLoadingInstance"
          :selectedList="selectedList"
          :setSelectedList="setSelectedList"
          class="overflow-auto pr-1"
          tableName="milestoneGroupsTable"
        />
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { BIconPlus, BIconSearch } from 'bootstrap-vue';
import api from '../../../../api/index';

import { DeleteRowMixin, NotificationMixin, AuthMixin } from '../../../../../mixins';
import PageHeader from '../../../common/page-header';
import Table from '../../../common/table';
import SearchInput from '../../../common/search-input';
import DeleteIcon from '../../../../../assets/icons/delete.svg';

let inputInterval = null;

export default {
  title: 'Milestones groups',
  components: { PageHeader, BIconPlus, BIconSearch, Table, SearchInput },
  mixins: [DeleteRowMixin, NotificationMixin, AuthMixin],
  data() {
    return {
      deleteIconUrl: DeleteIcon,
      selectedList: [],
      loadingInstance: null,
      filter: {
        page: 1,
        limit: 10,
        totalCount: 0,
        sortBy: '',
        sortOrder: '',
        searchQuery: '',
      },
      columns: [
        {
          field: 'check',
          key: 'check',
          type: 'checkbox',
          title: '',
          width: 50,
          align: 'center',
        },
        {
          field: 'name',
          key: 'name',
          title: this.$translate('Name'),
          sortBy: '',
          align: 'left',
        },
        {
          field: 'items',
          key: 'items',
          title: this.$translate('Milestones'),
          sortBy: '',
          align: 'left',
          renderBodyCell: ({ row }) => {
            return row.items.map((i) => i.name).join(', ');
          },
        },
        {
          field: 'actions',
          key: 'actions',
          title: '',
          width: 50,
          renderBodyCell: ({ row }) => {
            return (
              <div class="action-block">
                <div class="copy-button" on-click={() => this.goToDetails(row.id)}>
                  {this.$translate('Details')}
                </div>
              </div>
            );
          },
        },
      ],
      rows: [],
    };
  },
  methods: {
    async goToDetails(id) {
      this.$router.push({ name: 'milestone-group', params: { id } });
    },
    async goToAdd() {
      this.$router.push({ name: 'create-milestone-group' });
    },
    updateFilter(f) {
      this.filter = {
        ...this.filter,
        ...f,
      };
    },
    setSelectedList(list) {
      this.selectedList = list;
    },
    setLoadingInstance(loadingInstance) {
      this.loadingInstance = loadingInstance;
    },
    searchChange(e) {
      const v = e.target.value;
      this.filter.searchQuery = v;
      if (inputInterval) {
        clearTimeout(inputInterval);
      }
      inputInterval = setTimeout(() => {
        this.getPage();
      }, 500);
    },
    startLoading() {
      this.loadingInstance.show();
    },
    stopLoading() {
      this.loadingInstance.close();
    },
    async deleteGroup() {
      await this.deleteSelectedRows(null, async () => {
        try {
          const ids = this.selectedList.map((r) => r.id);
          await api.milestoneGroup.remove({ ids });
          this.rows = this.rows.filter((i) => !ids.includes(i.id));
          this.selectedList = [];
          this.getPage();
        } catch (err) {
          this.stopLoading();
          this.showNotification({ type: 'error', message: err.message });
        }
      });
    },
    async getPage() {
      try {
        this.startLoading();
        const { page, limit, sortBy, sortOrder, status, dateRange, searchQuery } = this.filter;
        const ctx = {
          page,
          limit,
          sortBy,
          sortOrder,
          searchQuery,
        };
        const response = await api.milestoneGroup.get(ctx);
        this.filter.totalCount = response.totalCount;
        this.stopLoading();
        this.rows = response.data;
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ve-table-body {
  ::v-deep th {
    color: rgb(112, 112, 112) !important;
    background-color: rgb(239, 239, 239) !important;
    border: none !important;
    min-height: 50px;
  }
  ::v-deep td {
    color: rgb(112, 112, 112) !important;
    cursor: pointer;
  }
}

::v-deep .ve-table-cell-selection {
  border: none !important;
}

::v-deep .ve-table-sort {
  top: -4.5px;
}

::v-deep .details-action {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 28px;
  margin: auto;
}

.statuses-block {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.date-range-block {
  min-width: 350px;
  text-align: center;
  margin-left: 30px;
  display: flex;
  align-items: center;
}

::v-deep .ve-select-selected-span {
  font-weight: 400;
}

.pagination-block {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

.action-block {
  display: flex;
}

::v-deep .copy-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  color: rgb(49, 141, 255);
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  margin-right: 20px;
  cursor: pointer;
}

::v-deep .delete-button {
  background: rgb(219, 71, 79);
  color: white;
  border-color: #db474f;
  border: 2px solid #db474f;
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  cursor: pointer;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
